import React, { useMemo } from "react";
import GroupedTreeTable, {
  FIRST_COLUMN_SORT_KEY,
  TreeExpansion,
  TreeTableColumn,
} from "../../common/GroupedTreeTable";
import { ReportMetricDef, ViewSort } from "../../../types/reportsV2";
import { ReportTableModel } from "./hooks/useReportTable";
import MetricTooltip, { hasMetricHelp } from "./MetricTooltip";

export interface ReportTreeTableProps {
  ariaLabel: string;
  table: ReportTableModel;
  expansion: TreeExpansion;
  sort: ViewSort | null;
  onSortChange: (sort: ViewSort | null) => void;
}

/** A column's first sort direction: numbers high→low, labels A→Z. */
export const firstSortDir = (key: string): ViewSort["dir"] =>
  key === FIRST_COLUMN_SORT_KEY ? "asc" : "desc";

/**
 * Header click cycle: first click sorts numbers high→low (labels A→Z), the
 * second reverses, the third returns to the default order.
 */
export function nextSort(
  current: ViewSort | null,
  key: string,
): ViewSort | null {
  const first = firstSortDir(key);
  if (!current || current.key !== key) return { key, dir: first };
  if (current.dir === first) {
    return { key, dir: first === "asc" ? "desc" : "asc" };
  }
  return null;
}

const toColumn = (metric: ReportMetricDef): TreeTableColumn => ({
  key: metric.key,
  label: metric.label,
  info: hasMetricHelp(metric) ? <MetricTooltip metric={metric} /> : undefined,
});

/**
 * The Daily Sales Report table: date buckets at depth 0 with the chosen
 * groupings nested below, metric headers with the definition's description
 * and formula as tooltips, a sticky totals row, and client-side sorting.
 */
export default function ReportTreeTable({
  ariaLabel,
  table,
  expansion,
  sort,
  onSortChange,
}: ReportTreeTableProps) {
  const columns = useMemo(() => table.columns.map(toColumn), [table.columns]);
  const totals = useMemo(
    () => ({ label: "Total", cells: table.totalsCells }),
    [table.totalsCells],
  );

  return (
    <GroupedTreeTable
      ariaLabel={ariaLabel}
      firstColumnLabel={table.firstColumnLabel}
      columns={columns}
      nodes={table.nodes}
      totals={totals}
      expansion={expansion}
      sort={sort}
      // No explicit sort = dates newest first.
      defaultSortDir="desc"
      onSort={(key) => onSortChange(nextSort(sort, key))}
      leafMuted
      emptyState="No results for these filters"
    />
  );
}
